import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { billingApi } from "./billingApi";
import { BillingCode, describeBillingError } from "./billingCodes";
import { describePromoPreview } from "./promoPreview";
import "./index.scss";

// A promo code is checked before anybody commits to it: what it takes off, for
// how long, and what the first paid invoice then comes to — the server's
// figures, not ones worked out here. The code itself is still sent with the
// subscription or the plan change; checking is only ever a preview.

const REFUSED_CODES = new Set([BillingCode.PROMO_CODE_INVALID, BillingCode.PROMO_CODE_NOT_APPLICABLE]);

function PromoCodePreview({ organizationId, packetId, isMonthly, promoCode, trialing = true }) {
   const [preview, setPreview] = useState({ state: "idle" });
   const code = typeof promoCode === "string" ? promoCode.trim() : "";

   // A preview is of one code, on one plan, on one billing period. Change any of
   // them and it no longer describes what would be bought.
   useEffect(() => {
      setPreview({ state: "idle" });
   }, [organizationId, packetId, isMonthly, code]);

   if (!code) return null;

   const checkCode = async () => {
      if (!packetId || preview.state === "checking") return;

      setPreview({ state: "checking" });

      try {
         const result = await billingApi.previewPromoCode({
            organizationId,
            packetId,
            isMonthlySubscription: isMonthly,
            promoCode: code
         });

         setPreview(
            result?.valid
               ? { state: "valid", lines: describePromoPreview(result, { trialing }) }
               : {
                    state: "refused",
                    message:
                       result?.error || describeBillingError({ code: BillingCode.PROMO_CODE_INVALID })
                 }
         );
      } catch (failure) {
         setPreview({
            state: REFUSED_CODES.has(failure.code) ? "refused" : "failed",
            message: describeBillingError(failure)
         });
      }
   };

   return (
      <div className="billing-promo" aria-live="polite">
         {preview.state !== "valid" && (
            <Button
               className="defaultButton"
               onClick={checkCode}
               loading={preview.state === "checking"}
               disabled={!packetId}
            >
               Check code
            </Button>
         )}

         {!packetId && <span className="billing-note">Pick a plan to check the code against it.</span>}

         {preview.state === "valid" && (
            <div className="billing-promo__result billing-promo__result--valid">
               <b>{preview.lines.headline}</b>
               <span>{preview.lines.timing}</span>
               {preview.lines.amounts && <span>{preview.lines.amounts}</span>}
            </div>
         )}

         {(preview.state === "refused" || preview.state === "failed") && (
            <div className="billing-promo__result billing-promo__result--refused" role="alert">
               {preview.message}
            </div>
         )}
      </div>
   );
}

export default PromoCodePreview;
